<script setup lang="ts">
import { defineProps, ref, computed } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';

import type { Product, ProductOption } from '../utils/types';

const props = defineProps<{
  product: Product | null;
}>();

const activePanels = ref<string | null>(null);

const hasValidValue = (option: ProductOption): boolean => {
  return option.value !== null && option.value !== 'BRAK' && option.value !== '';
};

const isEmptyProduct = (productName: string | undefined): boolean => {
  if (!productName) return true;
  const normalizedName = productName.toLowerCase().trim();
  return normalizedName === 'brak' || normalizedName === '' || normalizedName.includes('brak');
};

const hasValidMainOptions = computed(() => {
  if (!props.product) return false;
  if (isEmptyProduct(props.product.name)) return false;
  return props.product.product_options?.some(hasValidValue) ?? false;
});

const validProductGroups = computed(() => {
  return (
    props.product?.product_groups?.filter((group) => {
      if (isEmptyProduct(group.product?.name)) return false;
      return group.product?.product_options?.some(hasValidValue);
    }) ?? []
  );
});

const shouldShowAccordion = computed(() => {
  return hasValidMainOptions.value || validProductGroups.value.length > 0;
});
</script>

<template>
  <Accordion v-if="shouldShowAccordion" class="mt-6" v-model:value="activePanels">
    <AccordionPanel value="parameters">
      <AccordionHeader>Parametry</AccordionHeader>
      <AccordionContent class="space-y-6">
        <article v-if="product?.product_options?.length" class="mt-4 space-y-1">
          <header class="font-bold text-gray-900">Parametry {{ product.name }}</header>

          <div class="divide-y">
            <div
              :key="option.id"
              v-for="option in product?.product_options"
              class="flex justify-between py-3 text-sm"
            >
              <span class="text-gray-iron-600">
                {{ option.name }}
              </span>
              <span class="max-w-md font-medium text-gray-900">{{ option.value }}</span>
            </div>
          </div>
        </article>

        <article
          :key="group.id"
          v-for="group in product?.product_groups.filter(
            (group) => group.product?.product_options?.length
          )"
          class="mt-4 space-y-1"
        >
          <header class="font-bold text-gray-900">Parametry {{ group.product.name }}</header>

          <div class="divide-y">
            <div
              :key="option.id"
              v-for="option in group.product.product_options"
              class="flex justify-between py-3 text-sm"
            >
              <span class="text-gray-iron-600">
                {{ option.name }}
              </span>
              <span class="max-w-md font-medium text-gray-900">{{ option.value }}</span>
            </div>
          </div>
        </article>
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<style scoped>
:deep(.p-accordionheader) {
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.semibold');
}
</style>
